import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Paper, Button, Typography, Alert } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { TextInput } from '../../components/inputs';
import { PrimaryButton } from '../../components/buttons';
import { apiPost } from '../../services/apiClient';

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [recoverEmail, setRecoverEmail] = useState<string | null>(null);
  const [step, setStep] = useState<'email' | 'password'>('email');

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const recoverParam = searchParams.get('recover');
    if (recoverParam) {
      setRecoverEmail(decodeURIComponent(recoverParam));
      setStep('password');
    } else {
      setStep('email');
    }
  }, [searchParams]);

  const emailRegex = /^\S+@\S+\.\S+$/;
  const isEmailValid = emailRegex.test(email);

  const validatePassword = (password: string): string | null => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!/(?=.*[a-z])/.test(password)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/(?=.*[A-Z])/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/(?=.*\d)/.test(password)) {
      return 'Password must contain at least one number';
    }
    return null;
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      setMessage({ type: 'error', text: 'Email is required' });
      return;
    }

    if (!isEmailValid) {
      setMessage({ type: 'error', text: 'Please enter a valid email address' });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      await apiPost(
        process.env.REACT_APP_API_URL + `/api/v1/users/reset/${encodeURIComponent(email.trim())}`,
        {},
        { skipAuth: true }
      );

      setMessage({
        type: 'success',
        text: 'Password reset instructions have been sent to your email address.'
      });
      setEmail('');
    } catch (_) {
      setMessage({
        type: 'error',
        text: 'Something went wrong! Please try again later.'
      });
    } finally {
      setLoading(false);
    }
  };


  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!recoveryCode.trim()) {
      setMessage({ type: 'error', text: 'Recovery code is required' });
      return;
    }

    if (!newPassword || !confirmPassword) {
      setMessage({ type: 'error', text: 'All password fields are required' });
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'Passwords do not match' });
      return;
    }

    const passwordError = validatePassword(newPassword);
    if (passwordError) {
      setMessage({ type: 'error', text: passwordError });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      if (!recoverEmail) {
        throw new Error('No recovery email provided');
      }
      await apiPost(
        process.env.REACT_APP_API_URL + `/api/v1/users/recover/${encodeURIComponent(recoverEmail)}`,
        {
          password: newPassword,
          reset_key: recoveryCode,
        },
        { skipAuth: true }
      );

      setMessage({
        type: 'success',
        text: 'Password reset successfully! You can now log in with your new password.'
      });

      setTimeout(() => {
        navigate('/login');
      }, 3000);

    } catch (_) {
      setMessage({
        type: 'error',
        text: 'Something went wrong! Please try again later.'
      });
    } finally {
      setLoading(false);
    }
  };

  if (step === 'password' && !recoverEmail) {
    return <div>Loading...</div>;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'background.lightGrey',
        padding: 2,
        position: 'relative',

      }}>
      <Paper
        elevation={8}
        sx={{
          width: '100%',
          maxWidth: 420,
          padding: 4,
          backgroundColor: 'background.white',
          borderRadius: 1.5,
          border: '1px solid border.default',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
          textAlign: 'center',
          position: 'relative',
        }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/login')}
          sx={{
            color: 'text.grey',
            textTransform: 'none',
            fontSize: (theme: any) => theme.typography.fontSizes.body,
            fontWeight: 500,
            mb: 3,
            position: 'relative',
            zIndex: 1,
            '&:hover': {
              color: 'text.dark',
              backgroundColor: 'transparent',
            },
          }}
        >
                    Back to Login
        </Button>

        <Box
          component="img"
          src="/black-logo.png"
          alt="Company Logo"
          sx={{
            display: 'block',
            mx: 'auto',
            mb: 3,
            width: 140,
            height: 'auto',
            position: 'relative',
            zIndex: 1,
          }}
        />

        <Typography
          variant="h5"
          component="h2"
          color="text.primary"
          mb={2}
          sx={{
            fontWeight: 600,
            color: 'text.darkGrey',
            position: 'relative',
            zIndex: 1,
          }}
        >
          {step === 'email' ? 'Reset Password' : 'Set New Password'}
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          mb={4}
          sx={{
            color: 'text.grey',
            position: 'relative',
            zIndex: 1,
          }}
        >
          {step === 'email'
            ? 'Enter your email address and we\'ll send you instructions to reset your password.'
            : `Please enter your recovery code and new password for ${recoverEmail}.`
          }
        </Typography>

        {message && (
          <Alert
            severity={message.type}
            sx={{
              mb: 3,
              borderRadius: 2,
              position: 'relative',
              zIndex: 1,
            }}
            onClose={() => setMessage(null)}
          >
            {message.text}
          </Alert>
        )}

        <Box component="form" onSubmit={step === 'email' ? handleEmailSubmit : handlePasswordSubmit} sx={{ position: 'relative', zIndex: 1 }}>
          {step === 'email' ? (
            <TextInput
              fullWidth
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email address"
              disabled={loading}
              sx={{ mb: 4 }}
            />
          ) : (
            <>
              <TextInput
                fullWidth
                label="Recovery Code"
                value={recoveryCode}
                onChange={(e) => setRecoveryCode(e.target.value)}
                placeholder="Enter recovery code"
                disabled={loading}
                sx={{ mb: 3 }}
              />
              <TextInput
                fullWidth
                label="New Password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                disabled={loading}
                sx={{ mb: 3 }}
              />
              <TextInput
                fullWidth
                label="Confirm New Password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                disabled={loading}
                sx={{ mb: 4 }}
              />
            </>
          )}

          <PrimaryButton
            type="submit"
            fullWidth
            loading={loading}
            disabled={step === 'email' && !isEmailValid}
          >
            {step === 'email' ? 'Send Reset Instructions' : 'Reset Password'}
          </PrimaryButton>
        </Box>

        <Box sx={{ textAlign: 'center', mt: 3, position: 'relative', zIndex: 1 }}>
          <Typography variant="body2" color="text.secondary">
                        Remember your password?{' '}
            <Button
              variant="text"
              onClick={() => navigate('/login')}
              sx={{
                color: 'text.dark',
                textTransform: 'none',
                fontSize: (theme: any) => theme.typography.fontSizes.body,
                fontWeight: 600,
                p: 0,
                minWidth: 'auto',
                '&:hover': {
                  backgroundColor: 'transparent',
                  textDecoration: 'underline',
                },
              }}
            >
                            Sign in
            </Button>
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default ForgotPasswordPage;
